import React, { useEffect, useState } from 'react';
import { cashCountApi } from '../api/cashCount';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import DocumentHeader from '../components/DocumentHeader';

const CashUp: React.FC = () => {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState<any>(null);
  const [counts, setCounts] = useState<any>({});
  const [notes, setNotes] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showMoves, setShowMoves] = useState(false);

  const fmt = (n:any) => Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});

  const load = () => {
    setLoading(true);
    cashCountApi.show({ date }).then(r => {
      const d = r.data.data;
      setData(d);
      const c:any = {}, n:any = {};
      (d.rows||[]).forEach((row:any) => {
        if (row.counted !== null) { c[row.account_id] = String(row.counted); n[row.account_id] = row.note || ''; }
      });
      setCounts(c); setNotes(n);
    }).catch(() => showToast('Could not load the day', 'error')).finally(() => setLoading(false));
  };
  useEffect(load, [date]); // eslint-disable-line

  const diffOf = (row:any) => {
    const typed = counts[row.account_id];
    if (typed === undefined || typed === '') return null;
    return Number(typed) - Number(row.expected);
  };

  const save = async () => {
    const payload = (data.rows||[])
      .filter((r:any) => counts[r.account_id] !== undefined && counts[r.account_id] !== '')
      .map((r:any) => ({ account_id:r.account_id, expected:r.expected, counted:Number(counts[r.account_id]), note:notes[r.account_id] || null }));
    if (!payload.length) { showToast('Enter what you counted', 'error'); return; }
    const unexplained = payload.find((p:any) => Math.abs(p.counted - p.expected) > 0.01 && !p.note);
    if (unexplained) { showToast('Explain any account that does not balance', 'error'); return; }
    setSaving(true);
    try { await cashCountApi.save({ date, counts: payload }); showToast('Count saved'); load(); }
    catch (e:any) { showToast(e.response?.data?.message || 'Could not save', 'error'); }
    finally { setSaving(false); }
  };

  const th: React.CSSProperties = { padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' };
  const thL: React.CSSProperties = { ...th, textAlign:'left' };
  const td: React.CSSProperties = { padding:'8px 10px', fontSize:12, textAlign:'right', fontFamily:'monospace' };

  const rows = data?.rows || [];
  const totalExpected = rows.reduce((s:number,r:any) => s + Number(r.expected), 0);
  const totalCounted  = rows.reduce((s:number,r:any) => s + Number(counts[r.account_id] || 0), 0);
  const totalDiff     = totalCounted - totalExpected;
  const anyTyped      = rows.some((r:any) => counts[r.account_id] !== undefined && counts[r.account_id] !== '');

  return (
    <div>
      <div className="page-header no-print">
        <h1 className="page-title">Daily Cash-Up</h1>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <input type="date" className="form-control" style={{ width:160 }} value={date} onChange={e => setDate(e.target.value)} />
          <button className="btn btn-secondary" onClick={() => window.print()}>Print</button>
          {hasPermission('cash_count.manage') &&
            <button className="btn btn-primary" onClick={save} disabled={saving || !anyTyped}>{saving ? 'Saving...' : 'Save count'}</button>}
        </div>
      </div>

      <DocumentHeader />

      <div style={{ textAlign:'center', marginBottom:14 }}>
        <div style={{ fontSize:15, fontWeight:600 }}>Daily Cash-Up</div>
        <div style={{ fontSize:12, color:'var(--tx3)' }}>{date}</div>
      </div>

      {loading ? <div className="empty-state"><p>Loading...</p></div>
      : rows.length === 0 ? <div className="empty-state"><p>No money moved on this day.</p></div>
      : <>
        <div className="card" style={{ marginBottom:12 }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'var(--sur2)' }}>
              <th style={thL}>Account</th><th style={th}>Opening</th><th style={th}>In</th><th style={th}>Out</th>
              <th style={th}>Expected</th><th style={th}>Counted</th><th style={th}>Difference</th><th style={thL}>Why</th>
            </tr></thead>
            <tbody>
              {rows.map((r:any) => {
                const d = diffOf(r);
                return (
                  <tr key={r.account_id} style={{ borderBottom:'1px solid var(--bdr)' }}>
                    <td style={{ padding:'8px 10px', fontSize:12 }}>{r.account_code} {r.account_name}</td>
                    <td style={{...td, color:'var(--tx3)'}}>{fmt(r.opening)}</td>
                    <td style={{...td, color:'var(--green)'}}>{Number(r.money_in) ? fmt(r.money_in) : '-'}</td>
                    <td style={{...td, color:'var(--red)'}}>{Number(r.money_out) ? fmt(r.money_out) : '-'}</td>
                    <td style={{...td, fontWeight:600}}>{fmt(r.expected)}</td>
                    <td style={{ padding:'4px 6px', width:120 }}>
                      <input className="form-control mono" type="text" inputMode="decimal" style={{ textAlign:'right' }}
                        disabled={!hasPermission('cash_count.manage')}
                        value={counts[r.account_id] ?? ''} placeholder="count"
                        onChange={e => setCounts({ ...counts, [r.account_id]: e.target.value })} />
                    </td>
                    <td style={{...td, fontWeight:600, color: d === null ? 'var(--tx3)' : Math.abs(d) < 0.01 ? 'var(--green)' : 'var(--red)'}}>
                      {d === null ? '-' : fmt(d)}
                    </td>
                    <td style={{ padding:'4px 6px', minWidth:160 }}>
                      {d !== null && Math.abs(d) > 0.01 &&
                        <input className="form-control" style={{ fontSize:12 }} placeholder="explain the difference"
                          value={notes[r.account_id] ?? ''} onChange={e => setNotes({ ...notes, [r.account_id]: e.target.value })} />}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ background:'var(--sur2)', fontWeight:700 }}>
                <td style={{ padding:'10px' }}>Total</td>
                <td></td><td></td><td></td>
                <td style={td}>{fmt(totalExpected)}</td>
                <td style={td}>{anyTyped ? fmt(totalCounted) : '-'}</td>
                <td style={{...td, color: !anyTyped ? 'var(--tx3)' : Math.abs(totalDiff) < 0.01 ? 'var(--green)' : 'var(--red)'}}>
                  {anyTyped ? fmt(totalDiff) : '-'}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="no-print" style={{ marginBottom:10 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowMoves(s => !s)}>
            {showMoves ? 'Hide' : 'Show'} the day's movements ({(data.movements||[]).length})
          </button>
        </div>

        {showMoves && (
          <div className="card">
            <div className="card-header"><span>What made up the day</span></div>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'var(--sur2)' }}>
                <th style={thL}>Type</th><th style={thL}>Account</th><th style={thL}>Contact</th>
                <th style={thL}>Note</th><th style={th}>In</th><th style={th}>Out</th><th style={thL}>By</th>
              </tr></thead>
              <tbody>
                {(data.movements||[]).map((m:any,i:number) => (
                  <tr key={i} style={{ borderBottom:'1px solid var(--bdr)' }}>
                    <td style={{ padding:'6px 10px', fontSize:11 }}>{m.type}</td>
                    <td style={{ padding:'6px 10px', fontSize:11 }}>{m.account_code}</td>
                    <td style={{ padding:'6px 10px', fontSize:11, color:'var(--tx3)' }}>{m.contact_name || '-'}</td>
                    <td style={{ padding:'6px 10px', fontSize:11, color:'var(--tx2)' }}>{m.description}</td>
                    <td style={{...td, fontSize:11, color:'var(--green)'}}>{Number(m.debit) ? fmt(m.debit) : '-'}</td>
                    <td style={{...td, fontSize:11, color:'var(--red)'}}>{Number(m.credit) ? fmt(m.credit) : '-'}</td>
                    <td style={{ padding:'6px 10px', fontSize:10, color:'var(--tx3)' }}>{m.by_name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop:28, display:'flex', gap:40, fontSize:12 }}>
          <div>Counted by: ______________________</div>
          <div>Checked by: ______________________</div>
        </div>
      </>}
    </div>
  );
};

export default CashUp;
